/**
 * IANA zones whose DST rules changed recently enough that out-of-date browsers (notably
 * locked-down kiosks) still apply the old rules, mapped to an equivalent fixed-offset zone.
 *
 * Fixed-offset `Etc/GMT` zones never change, so they resolve correctly regardless of the
 * tzdata version the browser ships. Note their signs are inverted: `Etc/GMT+7` is UTC−7.
 *
 * Only add a zone here once it has settled on a single year-round offset - a fixed-offset
 * zone cannot express DST transitions.
 */
const FIXED_OFFSET_ZONES: Record<string, string> = {
  // Each of these stayed on its 2026 summer offset and skips the 2026-11-01 fall-back.
  // Browsers with tzdata older than the listed release still fall back.

  // British Columbia: permanent UTC−7 (tzdata 2026b, 2026-04-22)
  'America/Vancouver': 'Etc/GMT+7',
  // Alberta, plus most of the Northwest Territories: permanent UTC−6 (tzdata 2026c, 2026-07-08)
  'America/Edmonton': 'Etc/GMT+6',
  // Northwest Territories (Inuvik): permanent UTC−6 (tzdata 2026d, 2026-09-11)
  'America/Inuvik': 'Etc/GMT+6',
  // Manitoba: permanent UTC−5 (tzdata 2026e, 2026-09-29)
  'America/Winnipeg': 'Etc/GMT+5',
};

/**
 * Returns the zone that should be used to evaluate venue-local times for the given venue
 * timezone - a fixed-offset replacement when one is defined, otherwise the zone unchanged.
 */
export function resolveVenueTimezone<T extends string | null | undefined>(tz: T): T | string {
  if (!tz || !FIXED_OFFSET_ZONES[tz]) {
    return tz;
  }

  return FIXED_OFFSET_ZONES[tz];
}
